<script lang="ts" setup>
import type { IntegrationConfig } from "@kaleidoscope-solutions/engine-wasm"
import { KButton, KTextInput } from "@kaleidoscope-solutions/ks-design-system"
import {
  CheckRingIcon,
  InfoIcon,
} from "@kaleidoscope-solutions/ks-icons/vue/fill"
import { computed, ref, toRef, watch } from "vue"

interface Props {
  currentIntegration?: IntegrationConfig
}

interface Emits {
  (event: "submit", token: string, storeName: string): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const token = ref<string>("")
const tokenDirty = ref(false)
const storeName = ref<string>("")
const storeNameDirty = ref(false)
const tokenValid = ref(true)
const storeNameValid = ref(true)
const currentIntegration = toRef(props, "currentIntegration")

// TODO(Matt): This is not actually a valid connection, purely a saved token.
const validConnection = computed(() => {
  return currentIntegration.value?.token_status === "TokenPresent"
})

const canConnect = computed(() => {
  const isValidToken = validateToken(token.value)
  const isValidStoreName = validateToken(storeName.value)

  return isValidToken && isValidStoreName
})

const storeNameChanged = computed(() => {
  const currentStoreName = currentIntegration.value?.store_name ?? ""
  return storeName.value !== currentStoreName
})

const tokenChanged = computed(() => {
  const currentToken = currentIntegration.value?.access_token ?? ""
  return token.value !== currentToken
})

watch(
  currentIntegration,
  (integration) => {
    if (integration !== undefined) {
      storeName.value = integration.store_name
    }
  },
  {
    immediate: true,
  }
)

watch(storeName, () => {
  if (!storeNameChanged.value) {
    storeNameDirty.value = false
    if (!tokenChanged.value) {
      tokenDirty.value = false
    }

    return
  }

  storeNameDirty.value = true

  if (token.value) {
    return
  }

  tokenDirty.value = true
})

watch(token, () => (tokenDirty.value = tokenChanged.value))

function handleSubmit() {
  // send to engine await for response to show success
  emit("submit", token.value, storeName.value)
  tokenDirty.value = false
  storeNameDirty.value = false
}

function validateToken(token: string) {
  return token !== ""
}

function validateUrl(url: string) {
  return url !== ""
}

function handleConnect() {
  tokenValid.value = validateToken(token.value)
  storeNameValid.value = validateUrl(storeName.value)

  // submit
  if (tokenValid.value && storeNameValid.value) {
    handleSubmit()
  }
}
</script>

<template>
  <div class="w-full">
    <h2 class="text-lg font-semibold">
      Connect to Shopify
    </h2>

    <div class="my-2 flex min-h-96 rounded-md bg-gray-100 p-9">
      <form>
        <fieldset class="pb-8">
          <label>Store URL</label>

          <KTextInput
            v-model="storeName"
            :valid="false"
            :warning="!storeNameValid"
            class="pt-2 md:min-w-96"
          />
          <p class="pb-4 pt-2 text-sm">
            <span v-if="storeName">{{ `${storeName}.myshopify.com` }}</span>
            <span v-else>e.g. [your-shop-name].myshopify.com</span>
          </p>
          <p
            v-if="!storeNameValid"
            class="flex items-center gap-x-2 text-sm"
          >
            <InfoIcon class="h-6 w-6 text-red-500" />
            <span>Please enter a valid Store URL</span>
          </p>
        </fieldset>

        <fieldset class="pb-8">
          <label class="">Storefront access token</label>

          <KTextInput
            v-model="token"
            :valid="false"
            :warning="!tokenValid"
            class="pt-2 md:min-w-96"
            :placeholder="
              validConnection && !tokenDirty ? `${'&bull;'.repeat(38)}` : ''
            "
            :class="{
              'placeholder:text-gray-900': validConnection,
            }"
          />
          <p class="pb-4 pt-2 text-sm">
            <a
              href="https://kaleidoscope.slite.page/p/eerl4P0Ne57oQZ/Your-Shopify-store-token"
              class="text-violet-500 underline hover:no-underline"
              target="_blank"
            >
              Get help on how to access your store token
            </a>
          </p>
          <p
            v-if="!tokenValid"
            class="flex items-center gap-x-2 text-sm"
          >
            <InfoIcon class="h-6 w-6 text-red-500" />
            <span>Please enter a valid token</span>
          </p>
        </fieldset>

        <div class="flex gap-x-5">
          <KButton
            :disabled="!canConnect"
            variety="primary"
            color="violet"
            @click="handleConnect"
          >
            Save
          </KButton>
          <p
            v-if="validConnection && !storeNameDirty && !tokenDirty"
            class="flex items-center gap-x-2 text-sm"
          >
            <CheckRingIcon class="h-6 w-6 text-green-500" />
            <span>Configuration saved</span>
          </p>
        </div>
      </form>
    </div>
  </div>
</template>
